"use client";

import Image from "next/image";
import { Check, Lock } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";

import type {
  DeliveryRow,
  DeliveryStageCopy,
  DeliveryStep,
} from "./delivery-stage-copy";

/** Seconds each deliverable stays at the front of the deck. */
const STEP_SECONDS = 5;

type CcDeliveryStageProps = {
  stage: DeliveryStageCopy;
  /** Id of the checklist next to the stage; its rows light up in step and can be clicked. */
  checklistId: string;
};

/**
 * "Leveranser" on the service hubs: a 3D deck of realistic deliverables (report,
 * plan, live site, dashboard, flow, message) that deals one card at a time in
 * step with the checklist, tilts with the pointer and pauses off screen.
 */
export function CcDeliveryStage({ stage, checklistId }: CcDeliveryStageProps) {
  const rootRef = useRef<HTMLElement>(null);
  const [step, setStep] = useState(0);
  const [inView, setInView] = useState(false);
  const count = stage.steps.length;

  const next = useCallback(
    () => setStep((current) => (current + 1) % count),
    [count],
  );

  // Play only while the stage is on screen.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      {
        threshold: 0.35,
      },
    );
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  // Light up the matching checklist row, and let the rows pick a card.
  useEffect(() => {
    const rows = document.querySelectorAll<HTMLLIElement>(
      `#${checklistId} > li`,
    );
    rows.forEach((row, index) => {
      row.dataset.active = String(index === step);
      row.dataset.done = String(index < step);
    });
  }, [checklistId, step]);

  useEffect(() => {
    const rows = Array.from(
      document.querySelectorAll<HTMLLIElement>(`#${checklistId} > li`),
    );
    const handlers = rows.map((row, index) => {
      const handler = () => setStep(index % count);
      row.addEventListener("click", handler);
      return handler;
    });
    return () =>
      rows.forEach((row, index) =>
        row.removeEventListener("click", handlers[index]),
      );
  }, [checklistId, count]);

  // Pointer tilt: the deck leans a few degrees towards the cursor.
  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    const root = rootRef.current;
    if (!root || event.pointerType !== "mouse") return;
    const box = root.getBoundingClientRect();
    const x = (event.clientX - box.left) / box.width - 0.5;
    const y = (event.clientY - box.top) / box.height - 0.5;
    root.style.setProperty("--tx", x.toFixed(3));
    root.style.setProperty("--ty", y.toFixed(3));
  };

  const onPointerLeave = () => {
    rootRef.current?.style.setProperty("--tx", "0");
    rootRef.current?.style.setProperty("--ty", "0");
  };

  const active = stage.steps[step];

  return (
    <figure
      ref={rootRef}
      className="cc-dl"
      data-inview={inView}
      style={{ "--dur": `${STEP_SECONDS}s` } as CSSProperties}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      aria-hidden="true"
    >
      <span className="cc-dl__glow" />
      <span className="cc-dl__lines" />

      <div className="cc-dl__deck">
        {stage.steps.map((item, index) => {
          const pos = (index - step + count) % count;
          return (
            <article
              key={index}
              className={`cc-dl__card cc-dl__card--${item.type}`}
              data-pos={pos === count - 1 ? "out" : pos > 3 ? "hidden" : pos}
            >
              <header className="cc-dl__bar">
                <i className="cc-dl__dots">
                  <b />
                  <b />
                  <b />
                </i>
                <span className="cc-dl__app">
                  {item.logo ? (
                    <Image
                      src={item.logo}
                      alt=""
                      width={16}
                      height={16}
                      unoptimized
                    />
                  ) : null}
                  {item.app}
                </span>
                {item.meta ? <em>{item.meta}</em> : null}
              </header>
              <div className="cc-dl__body">
                <p className="cc-dl__title">{item.title}</p>
                <StepBody step={item} />
              </div>
            </article>
          );
        })}
      </div>

      <div className="cc-dl__toast" key={step}>
        <Check size={13} strokeWidth={3} />
        <span>
          {stage.delivered}
          <small>{active.title}</small>
        </span>
      </div>

      <div className="cc-dl__hud">
        <span>
          {stage.label} {step + 1}/{count}
        </span>
        <ol>
          {stage.steps.map((_, index) => (
            <li
              key={index}
              data-state={
                index < step ? "done" : index === step ? "active" : "next"
              }
            >
              {index === step ? <i onAnimationEnd={next} /> : <i />}
            </li>
          ))}
        </ol>
      </div>

      <figcaption className="cc-dl__caption">{stage.caption}</figcaption>
    </figure>
  );
}

const stagger = (index: number) => ({ "--i": index }) as CSSProperties;

function Mark({ row }: { row: DeliveryRow }) {
  if (row.logo)
    return <Image src={row.logo} alt="" width={16} height={16} unoptimized />;
  if (row.badge) return <i className="cc-dl__badge">{row.badge}</i>;
  return <Check className="cc-dl__tick" size={13} strokeWidth={2.6} />;
}

function Rows({ rows }: { rows: DeliveryRow[] }) {
  return (
    <ul className="cc-dl__rows">
      {rows.map((row, index) => (
        <li key={row.label} style={stagger(index)}>
          <Mark row={row} />
          <span>{row.label}</span>
          {row.value ? <b>{row.value}</b> : null}
          {row.status ? (
            <em data-tone={row.tone ?? "won"}>{row.status}</em>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** Smooth area path through the series, in a 100×40 box. */
function areaPath(series: number[]) {
  const max = Math.max(...series);
  const min = Math.min(...series);
  const span = max - min || 1;
  const points = series.map((value, index) => [
    (index / (series.length - 1)) * 100,
    36 - ((value - min) / span) * 30,
  ]);
  let line = `M${points[0][0]},${points[0][1]}`;
  for (let index = 1; index < points.length; index++) {
    const [x0, y0] = points[index - 1];
    const [x1, y1] = points[index];
    const mid = (x0 + x1) / 2;
    line += ` C${mid},${y0} ${mid},${y1} ${x1},${y1}`;
  }
  const last = points[points.length - 1];
  return { line, area: `${line} L100,40 L0,40 Z`, last };
}

function StepBody({ step }: { step: DeliveryStep }) {
  switch (step.type) {
    case "report": {
      const circumference = 2 * Math.PI * 26;
      return (
        <div className="cc-dl-report">
          <div className="cc-dl-report__score">
            <svg viewBox="0 0 64 64">
              <circle cx="32" cy="32" r="26" />
              <circle
                cx="32"
                cy="32"
                r="26"
                style={
                  {
                    "--c": circumference,
                    "--to": circumference * (1 - step.score / 100),
                  } as CSSProperties
                }
              />
            </svg>
            <b>{step.score}</b>
            <small>{step.scoreLabel}</small>
          </div>
          <Rows rows={step.rows} />
        </div>
      );
    }
    case "table":
      return (
        <div className="cc-dl-table">
          {step.columns ? (
            <p className="cc-dl-table__head">
              {step.columns.map((column) => (
                <span key={column}>{column}</span>
              ))}
            </p>
          ) : null}
          <Rows rows={step.rows} />
        </div>
      );
    case "chart": {
      const { line, area, last } = areaPath(step.series);
      return (
        <div className="cc-dl-chart">
          <p className="cc-dl-chart__kpi">
            <b>{step.value}</b>
            <span>{step.period}</span>
            <em>{step.change}</em>
          </p>
          <svg viewBox="0 0 100 40" preserveAspectRatio="none">
            <defs>
              <linearGradient id="cc-dl-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="currentColor" stopOpacity="0.28" />
                <stop offset="1" stopColor="currentColor" stopOpacity="0" />
              </linearGradient>
            </defs>
            {[10, 20, 30].map((y) => (
              <line key={y} x1="0" x2="100" y1={y} y2={y} />
            ))}
            <path className="cc-dl-chart__area" d={area} />
            <path className="cc-dl-chart__line" d={line} pathLength={1} />
          </svg>
          <span
            className="cc-dl-chart__dot"
            style={{ left: `${last[0]}%`, top: `${(last[1] / 40) * 100}%` }}
          />
        </div>
      );
    }
    case "site":
      return (
        <div className="cc-dl-site">
          <p className="cc-dl-site__url">
            <Lock size={9} strokeWidth={2.6} />
            {step.url}
          </p>
          <div className="cc-dl-site__page">
            <p className="cc-dl-site__nav">
              <b>{step.brand}</b>
              <i />
              <i />
              <i />
            </p>
            <strong>{step.heading}</strong>
            <span>{step.text}</span>
            <em>{step.cta}</em>
          </div>
          {step.scores ? (
            <ul className="cc-dl-site__scores">
              {step.scores.map((score, index) => (
                <li key={score.label} style={stagger(index)}>
                  <b>{score.value}</b>
                  <small>{score.label}</small>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      );
    case "flow":
      return (
        <div className="cc-dl-flow">
          <ol className="cc-dl-flow__nodes">
            {step.nodes.map((node, index) => (
              <li key={node.label} style={stagger(index)}>
                <span>
                  <Image
                    src={node.logo}
                    alt=""
                    width={22}
                    height={22}
                    unoptimized
                  />
                </span>
                <small>{node.label}</small>
                {index < step.nodes.length - 1 ? (
                  <i className="cc-dl-flow__wire" />
                ) : null}
              </li>
            ))}
          </ol>
          <Rows rows={step.rows} />
        </div>
      );
    case "message":
      return (
        <div className="cc-dl-message">
          <p className="cc-dl-message__from">
            {step.photo ? (
              <Image src={step.photo} alt="" width={32} height={32} />
            ) : (
              <i>{step.initials}</i>
            )}
            <span>
              <b>{step.from}</b>
              <small>{step.when}</small>
            </span>
            {step.status ? <em>{step.status}</em> : null}
          </p>
          <strong>{step.subject}</strong>
          <span className="cc-dl-message__body">{step.body}</span>
        </div>
      );
  }
}
